import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Checkbox,
  Autocomplete,
  InputAdornment,
  Skeleton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import { pricingModeOf, PricingMode } from '../../services/serviceService';
import { serviceApi, Service, ServiceConsumptionInput } from '../../services/serviceService';
import { inventoryApiService, Product } from '../../services/inventoryService';
import { useSalonContextStore } from '../../store/salonContextStore';
import { useAuthStore } from '../../store/authStore';
import { tokens } from '../../theme';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PillTone, useConfirm } from '../../components/ui';
import { formatMoney, formatNumber, formatDuration, countLabel, plural, toNumber } from '../../utils/format';

interface ConsumptionFormItem {
  enabled?: boolean;
  quantity: string;
  perClient: string;
  perMl: string;
  clientsCount: string;
  note: string;
}

const createEmptyConsumptionItem = (): ConsumptionFormItem => ({
  enabled: false,
  quantity: '',
  perClient: '',
  perMl: '',
  clientsCount: '',
  note: '',
});

interface ConsumptionComputation {
  hasAnyInput: boolean;
  hasDriver: boolean;
  hasValidClientsCount: boolean;
  calculatedQuantity: number | null;
}

const toStringValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
};

const toPositiveNumber = (value: string): number | undefined => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }
  return parsed;
};

const computeConsumption = (item?: ConsumptionFormItem): ConsumptionComputation => {
  const raw = (item as Partial<ConsumptionFormItem> | undefined) ?? {};
  const quantityRaw = toStringValue(raw.quantity);
  const perClientRaw = toStringValue(raw.perClient);
  const perMlRaw = toStringValue(raw.perMl);
  const clientsCountRaw = toStringValue(raw.clientsCount).trim();
  const noteRaw = toStringValue(raw.note);

  const quantity = toPositiveNumber(quantityRaw);
  const perClient = toPositiveNumber(perClientRaw);
  const perMl = toPositiveNumber(perMlRaw);
  const clientsCount = toPositiveNumber(clientsCountRaw);
  const hasClientsCountInput = clientsCountRaw.length > 0;

  const hasAnyInput =
    quantityRaw.trim().length > 0 ||
    perClientRaw.trim().length > 0 ||
    perMlRaw.trim().length > 0 ||
    hasClientsCountInput ||
    noteRaw.trim().length > 0;
  const hasDriver = Boolean(quantity || perClient || perMl);
  const hasValidClientsCount = !hasClientsCountInput || Boolean(clientsCount);

  const multiplier = clientsCount ? Math.floor(clientsCount) : 1;
  const calculatedQuantity = quantity ?? (perClient ? perClient * multiplier : perMl ? perMl * multiplier : null);

  return {
    hasAnyInput,
    hasDriver,
    hasValidClientsCount,
    calculatedQuantity,
  };
};

const NBSP = ' ';
const UNCATEGORIZED = 'Bez kategorii';

const moneyRange = (min: number, max: number): string => {
  if (min === max) return formatMoney(min);
  const decimals = Number.isInteger(min) && Number.isInteger(max) ? 0 : 2;
  return `${formatNumber(min, decimals)}–${formatMoney(max, { decimals })}`;
};

const durationRange = (min: number, max: number): string => {
  if (min === max) return formatDuration(min);
  if (min < 60 && max < 60) return `${min}–${max}${NBSP}min`;
  return `${formatDuration(min)} – ${formatDuration(max)}`;
};

// Lokalne odpowiedniki formatServicePrice/formatServiceDuration z polskim zapisem kwot
// („200 zł” zamiast „200.00 zł”).
const servicePriceLabel = (svc: Service): string => {
  const mode = pricingModeOf(svc);
  if (mode === 'VARIANTS') {
    const prices = (svc.variants || []).map((v) => toNumber(v.price));
    return moneyRange(Math.min(...prices), Math.max(...prices));
  }
  if (mode === 'RANGE') return moneyRange(toNumber(svc.price), toNumber(svc.priceMax));
  return formatMoney(svc.price);
};

const serviceDurationLabel = (svc: Service): string => {
  if (svc.variants && svc.variants.length > 0) {
    const times = svc.variants.map((v) => toNumber(v.duration));
    return durationRange(Math.min(...times), Math.max(...times));
  }
  return svc.durationMax != null
    ? durationRange(toNumber(svc.duration), toNumber(svc.durationMax))
    : formatDuration(svc.duration);
};

const PRICING_MODE_HINT: Record<PricingMode, string> = {
  FIXED: 'Jedna cena i jeden czas trwania.',
  RANGE: 'Cena i czas w przedziale „od–do”, gdy zależą od klienta, np. od długości włosów.',
  VARIANTS: 'Każdy wariant ma własną cenę i czas — np. długość włosów albo stopień pracownika.',
};

const formatQuantity = (value: number) => String(Number(value.toFixed(3))).replace('.', ',');

const unitAdornment = (unit: string) => ({
  endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
});

/** Sekcja w oknie: tytul, jedno zdanie opisu i pola. */
const DialogSection: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  first?: boolean;
  children: React.ReactNode;
}> = ({ title, description, first, children }) => (
  <Box sx={{ pt: first ? 0.5 : 2.5, pb: 2.5, borderTop: first ? 'none' : `1px solid ${tokens.line}` }}>
    <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.4 }}>{title}</Typography>
    {description && (
      <Typography component="div" sx={{ mt: 0.25, mb: 1.75, fontSize: '0.875rem', color: tokens.muted }}>
        {description}
      </Typography>
    )}
    {!description && <Box sx={{ mb: 1.75 }} />}
    {children}
  </Box>
);

export const SalonServicesPage: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  // Uwaga klienta 9: kategorie utworzone wczesniej maja sie podpowiadac.
  // Kategoria jest polem tekstowym uslugi, wiec liste budujemy z tego,
  // co juz istnieje w salonie — bez osobnego slownika w bazie.
  const usedCategories = useMemo(
    () =>
      Array.from(
        new Set(
          services
            .map((s) => (s.category || '').trim())
            .filter((c) => c.length > 0),
        ),
      ).sort((a, b) => a.localeCompare(b, 'pl')),
    [services],
  );
  const [salonId, setSalonId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const EMPTY_FORM = {
    name: '', description: '', price: '', duration: '', category: '',
    priceMax: '', durationMax: '',
  };
  const [form, setForm] = useState(EMPTY_FORM);
  // Tryb ceny (uwaga klienta 8). Wynika z danych, ale w formularzu trzymamy go
  // jawnie, bo uzytkownik wybiera go zanim wpisze wartosci.
  const [pricingMode, setPricingMode] = useState<PricingMode>('FIXED');
  const [variants, setVariants] = useState<{ id?: string; name: string; price: string; duration: string }[]>([]);
  const [consumptionDialog, setConsumptionDialog] = useState(false);
  const [consumptionLoading, setConsumptionLoading] = useState(false);
  const [consumptionSaving, setConsumptionSaving] = useState(false);
  const [consumptionService, setConsumptionService] = useState<Service | null>(null);
  const [consumptionProducts, setConsumptionProducts] = useState<Product[]>([]);
  const [consumptionForm, setConsumptionForm] = useState<Record<string, ConsumptionFormItem>>({});
  const [dialogProductsLoading, setDialogProductsLoading] = useState(false);
  const [dialogProducts, setDialogProducts] = useState<Product[]>([]);
  const [dialogConsumptionForm, setDialogConsumptionForm] = useState<Record<string, ConsumptionFormItem>>({});
  const [saving, setSaving] = useState(false);
  const { activeSalonId, loadSalons } = useSalonContextStore();
  const confirm = useConfirm();
  // Dodawanie, edycja, usuwanie i zuzycie produktow sa w API tylko dla wlasciciela
  // (i administratora) — pracownik widzi cennik bez przyciskow, ktore i tak skonczylyby sie bledem.
  const role = useAuthStore((st) => st.user?.role);
  const canManage = role === 'SALON_OWNER' || role === 'SUPER_ADMIN';

  useEffect(() => {
    loadSalons();
  }, [loadSalons]);

  useEffect(() => {
    const load = async () => {
      if (!activeSalonId) {
        setSalonId('');
        setServices([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setSalonId(activeSalonId);
        const svcs = await serviceApi.listBySalon(activeSalonId, true);
        setServices(svcs);
      } catch {
        setError('Nie udało się załadować usług');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeSalonId]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setPricingMode('FIXED');
    setVariants([]);
  };

  const prepareServiceDialogProducts = async (serviceId?: string) => {
    if (!salonId) {
      setDialogProducts([]);
      setDialogConsumptionForm({});
      return;
    }

    setDialogProductsLoading(true);
    try {
      const products = await inventoryApiService.getProducts({ salonId });
      const initialForm = products.reduce<Record<string, ConsumptionFormItem>>((acc, product) => {
        acc[product.id] = createEmptyConsumptionItem();
        return acc;
      }, {});

      if (serviceId) {
        const consumptions = await serviceApi.getConsumptions(serviceId);
        for (const item of consumptions) {
          initialForm[item.product.id] = {
            enabled: true,
            quantity: String(item.quantity),
            perClient: item.perClient ? String(item.perClient) : '',
            perMl: item.otherParams?.perMl ? String(item.otherParams.perMl) : '',
            clientsCount: item.otherParams?.clientsCount
              ? String(item.otherParams.clientsCount)
              : '',
            note: item.otherParams?.note ?? '',
          };
        }
      }

      setDialogProducts(products);
      setDialogConsumptionForm(initialForm);
    } catch {
      setError('Nie udało się wczytać produktów do konfiguracji usługi');
      setDialogProducts([]);
      setDialogConsumptionForm({});
    } finally {
      setDialogProductsLoading(false);
    }
  };

  const buildConsumptionItems = (
    sourceForm: Record<string, ConsumptionFormItem>,
    sourceProducts: Product[],
  ): ServiceConsumptionInput[] => {
    const items: ServiceConsumptionInput[] = [];

    for (const product of sourceProducts) {
      const value = sourceForm[product.id] ?? createEmptyConsumptionItem();
      const computation = computeConsumption(value);
      const isExplicitlyEnabled = Boolean(value.enabled);

      if (!isExplicitlyEnabled && !computation.hasAnyInput) {
        continue;
      }

      const perClientValue = Number(toStringValue((value as Partial<ConsumptionFormItem>).perClient));
      const perMlValue = Number(toStringValue((value as Partial<ConsumptionFormItem>).perMl));
      const clientsCountValue = Number(
        toStringValue((value as Partial<ConsumptionFormItem>).clientsCount),
      );
      const note = toStringValue((value as Partial<ConsumptionFormItem>).note).trim();

      const clientsCountNormalized =
        Number.isFinite(clientsCountValue) && clientsCountValue > 0
          ? Math.floor(clientsCountValue)
          : undefined;
      const hasPerClient = Number.isFinite(perClientValue) && perClientValue > 0;
      const hasPerMl = Number.isFinite(perMlValue) && perMlValue > 0;

      const otherParams = {
        ...(hasPerMl ? { perMl: perMlValue } : {}),
        ...(clientsCountNormalized ? { clientsCount: clientsCountNormalized } : {}),
        ...(note ? { note } : {}),
      };

      items.push({
        productId: product.id,
        quantity: Number((computation.calculatedQuantity ?? 1).toFixed(3)),
        ...(hasPerClient ? { perClient: perClientValue } : {}),
        ...(Object.keys(otherParams).length > 0 ? { otherParams } : {}),
      });
    }

    return items;
  };

  const handleOpenAdd = async () => {
    resetForm();
    setEditId(null);
    setDialog(true);
    await prepareServiceDialogProducts();
  };

  const handleOpenEdit = async (svc: Service) => {
    setForm({
      name: svc.name,
      description: svc.description || '',
      price: String(svc.price),
      duration: String(svc.duration),
      category: svc.category || '',
      priceMax: svc.priceMax != null ? String(svc.priceMax) : '',
      durationMax: svc.durationMax != null ? String(svc.durationMax) : '',
    });
    setPricingMode(pricingModeOf(svc));
    setVariants(
      (svc.variants || []).map((v) => ({
        id: v.id, name: v.name, price: String(v.price), duration: String(v.duration),
      })),
    );
    setEditId(svc.id);
    setDialog(true);
    await prepareServiceDialogProducts(svc.id);
  };

  const handleSave = async () => {
    setError('');
    try {
      // Kazdy tryb czysci pola pozostalych, zeby w bazie nie zostaly resztki
      // po poprzednim wyborze — inaczej usluga wygladalaby na widelki mimo
      // przelaczenia na cene stala.
      const data = {
        name: form.name,
        price: parseFloat(form.price),
        duration: parseInt(form.duration, 10),
        priceMax: pricingMode === 'RANGE' && form.priceMax ? parseFloat(form.priceMax) : null,
        durationMax:
          pricingMode === 'RANGE' && form.durationMax ? parseInt(form.durationMax, 10) : null,
        variants:
          pricingMode === 'VARIANTS'
            ? variants
                .filter((v) => v.name.trim() && v.price !== '' && v.duration !== '')
                .map((v) => ({
                  ...(v.id ? { id: v.id } : {}),
                  name: v.name.trim(),
                  price: parseFloat(v.price),
                  duration: parseInt(v.duration, 10),
                }))
            : [],
        ...(form.description ? { description: form.description } : {}),
        ...(form.category ? { category: form.category } : {}),
      };

      let targetServiceId = editId;

      if (editId) {
        await serviceApi.update(editId, data);
      } else {
        if (!salonId) {
          setError('Wybierz salon, aby dodać usługę');
          return;
        }
        const createdService = await serviceApi.create({ ...data, salonId });
        targetServiceId = createdService.id;
      }

      if (targetServiceId) {
        const items = buildConsumptionItems(dialogConsumptionForm, dialogProducts);
        await serviceApi.setConsumptions(targetServiceId, items);
      }

      setDialog(false);
      const updated = await serviceApi.listBySalon(salonId, true);
      setServices(updated);
    } catch {
      setError(editId ? 'Nie udało się zaktualizować usługi' : 'Nie udało się dodać usługi');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await serviceApi.delete(id);
      setServices((prev) => prev.filter((s) => s.id !== id));
    } catch {
      setError('Nie udało się usunąć usługi');
    }
  };

  const openConsumptions = async (svc: Service) => {
    if (!salonId) {
      setError('Wybierz salon, aby ustawić zużycie');
      return;
    }

    setError('');
    setConsumptionService(svc);
    setConsumptionDialog(true);
    setConsumptionLoading(true);

    try {
      const [productsData, consumptionsData] = await Promise.all([
        inventoryApiService.getProducts({ salonId }),
        serviceApi.getConsumptions(svc.id),
      ]);

      setConsumptionProducts(productsData);
      const initialForm = productsData.reduce<Record<string, ConsumptionFormItem>>((acc, product) => {
        acc[product.id] = createEmptyConsumptionItem();
        return acc;
      }, {});

      for (const item of consumptionsData) {
        initialForm[item.product.id] = {
          quantity: String(item.quantity),
          perClient: item.perClient ? String(item.perClient) : '',
          perMl: item.otherParams?.perMl ? String(item.otherParams.perMl) : '',
          clientsCount: item.otherParams?.clientsCount
            ? String(item.otherParams.clientsCount)
            : '',
          note: item.otherParams?.note ?? '',
        };
      }

      setConsumptionForm(initialForm);
    } catch {
      setError('Nie udało się wczytać zużycia dla usługi');
    } finally {
      setConsumptionLoading(false);
    }
  };

  const closeConsumptions = () => {
    setConsumptionDialog(false);
    setConsumptionService(null);
    setConsumptionProducts([]);
    setConsumptionForm({});
    setConsumptionLoading(false);
    setConsumptionSaving(false);
  };

  const saveConsumptions = async () => {
    if (!consumptionService) {
      return;
    }

    const invalidRows = consumptionProducts.filter((product) => {
      const computation = computeConsumption(consumptionForm[product.id]);
      return (
        computation.hasAnyInput &&
        (!computation.hasDriver || !computation.hasValidClientsCount || !computation.calculatedQuantity)
      );
    });

    if (invalidRows.length > 0) {
      setError(
        `Uzupełnij poprawnie parametry zużycia dla: ${invalidRows
          .slice(0, 3)
          .map((p) => p.name)
          .join(', ')}${invalidRows.length > 3 ? '...' : ''}`,
      );
      return;
    }

    const items = buildConsumptionItems(consumptionForm, consumptionProducts);

    setConsumptionSaving(true);
    try {
      await serviceApi.setConsumptions(consumptionService.id, items);
      closeConsumptions();
    } catch {
      setError('Nie udało się zapisać zużycia');
    } finally {
      setConsumptionSaving(false);
    }
  };

  const onSaveService = async () => {
    setSaving(true);
    try {
      await handleSave();
    } finally {
      setSaving(false);
    }
  };

  const askDelete = async (svc: Service) => {
    const ok = await confirm({
      title: 'Usunąć usługę z cennika?',
      details: (
        <>
          <strong>{svc.name}</strong>
          {` · ${servicePriceLabel(svc)} · ${serviceDurationLabel(svc)}`}
        </>
      ),
      description: 'Usługa zniknie z oferty salonu. Historia wizyt i sprzedaży zostanie zachowana.',
      confirmLabel: 'Usuń usługę',
      tone: 'danger',
    });
    if (ok) await handleDelete(svc.id);
  };

  const updateVariant = (index: number, patch: Partial<{ name: string; price: string; duration: string }>) =>
    setVariants((prev) => prev.map((x, j) => (j === index ? { ...x, ...patch } : x)));

  const updateDialogConsumption = (productId: string, patch: Partial<ConsumptionFormItem>) =>
    setDialogConsumptionForm((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] ?? createEmptyConsumptionItem()), ...patch },
    }));

  const updateConsumption = (productId: string, patch: Partial<ConsumptionFormItem>) =>
    setConsumptionForm((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] ?? createEmptyConsumptionItem()), ...patch },
    }));

  const groupsMap = new Map<string, Service[]>();
  services.forEach((svc) => {
    const key = (svc.category || '').trim() || UNCATEGORIZED;
    groupsMap.set(key, [...(groupsMap.get(key) || []), svc]);
  });
  const groups = Array.from(groupsMap.entries()).sort(([a], [b]) => {
    if (a === UNCATEGORIZED) return 1;
    if (b === UNCATEGORIZED) return -1;
    return a.localeCompare(b, 'pl');
  });

  const inactiveCount = services.filter((s) => !s.isActive).length;
  const headerDescription = services.length
    ? [
        countLabel(services.length, 'usługa', 'usługi', 'usług'),
        usedCategories.length ? countLabel(usedCategories.length, 'kategoria', 'kategorie', 'kategorii') : null,
        inactiveCount ? `${inactiveCount} ${plural(inactiveCount, 'nieaktywna', 'nieaktywne', 'nieaktywnych')}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Cennik salonu: ceny, czas trwania i produkty zużywane przy usługach.';

  const dialogSelectedCount = dialogProducts.filter((p) => dialogConsumptionForm[p.id]?.enabled).length;
  const hasInvalidConsumptionRows = consumptionProducts.some((product) => {
    const computation = computeConsumption(consumptionForm[product.id]);
    return (
      computation.hasAnyInput &&
      (!computation.hasDriver || !computation.hasValidClientsCount || !computation.calculatedQuantity)
    );
  });

  const priceSx = { fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } as const;
  const headCellSx = { fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted } as const;

  const renderServiceRow = (svc: Service) => {
    const mode = pricingModeOf(svc);
    const variantList = svc.variants || [];
    const price = servicePriceLabel(svc);
    const duration = serviceDurationLabel(svc);
    const modeLabel =
      mode === 'RANGE' ? 'widełki cenowe' : mode === 'VARIANTS' ? countLabel(variantList.length, 'wariant', 'warianty', 'wariantów') : null;

    return (
      <Box
        key={svc.id}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr) auto',
            md: `minmax(0, 1fr) 190px 170px${canManage ? ' 118px' : ''}`,
          },
          columnGap: { xs: 1.5, md: 3 },
          alignItems: 'start',
          px: { xs: 2, md: 2.5 },
          py: 1.75,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem', color: svc.isActive ? tokens.ink : tokens.muted }}>
              {svc.name}
            </Typography>
            {!svc.isActive && <StatusPill label="Nieaktywna" tone="neutral" size="small" />}
          </Stack>
          {svc.description && (
            <Typography variant="body2" sx={{ color: tokens.muted, mt: 0.25 }}>{svc.description}</Typography>
          )}

          {/* Na telefonie cena i czas sa pod nazwa zamiast w osobnych kolumnach. */}
          <Stack direction="row" alignItems="baseline" columnGap={1.5} rowGap={0.25} flexWrap="wrap" sx={{ display: { xs: 'flex', md: 'none' }, mt: 0.75 }}>
            <Typography sx={{ ...priceSx, fontSize: '0.9375rem' }}>{price}</Typography>
            <Typography sx={{ whiteSpace: 'nowrap', color: tokens.inkSoft, fontSize: '0.875rem' }}>{duration}</Typography>
            {modeLabel && <Typography variant="caption">({modeLabel})</Typography>}
          </Stack>

        </Box>

        <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right' }}>
          <Typography sx={{ ...priceSx, fontSize: '0.9375rem' }}>{price}</Typography>
          {modeLabel && <Typography variant="caption" component="div">{modeLabel}</Typography>}
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right' }}>
          <Typography sx={{ whiteSpace: 'nowrap', color: tokens.inkSoft, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums' }}>
            {duration}
          </Typography>
        </Box>

        {canManage && (
          <Stack direction="row" justifyContent="flex-end" gap={0.25} sx={{ mt: -0.5, mr: -0.75 }}>
            <Tooltip title="Zużycie produktów">
              <IconButton size="small" aria-label={`Zużycie produktów: ${svc.name}`} onClick={() => openConsumptions(svc)}>
                <Inventory2OutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edytuj usługę">
              <IconButton size="small" aria-label={`Edytuj usługę: ${svc.name}`} onClick={() => handleOpenEdit(svc)}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Usuń usługę">
              <IconButton
                size="small"
                aria-label={`Usuń usługę: ${svc.name}`}
                onClick={() => askDelete(svc)}
                sx={{ '&:hover': { color: tokens.danger, bgcolor: tokens.dangerSoft } }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {variantList.length > 0 && (
          <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / 2' }, mt: 1.25, maxWidth: 520, border: `1px solid ${tokens.line}`, borderRadius: '10px', bgcolor: tokens.surface, px: 1.5, py: 0.25 }}>
            {variantList.map((v, i) => (
              <Stack
                key={v.id || i}
                direction="row"
                alignItems="baseline"
                gap={1.5}
                sx={{ py: 0.75, '&:not(:last-of-type)': { borderBottom: `1px dashed ${tokens.lineStrong}` } }}
              >
                <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem', color: tokens.inkSoft }}>{v.name}</Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {formatDuration(v.duration)}
                </Typography>
                <Typography sx={{ ...priceSx, fontSize: '0.875rem', fontWeight: 650, minWidth: 64, textAlign: 'right' }}>
                  {formatMoney(v.price)}
                </Typography>
              </Stack>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const durationHint = (value: string) => (toNumber(value) >= 60 ? `To ${formatDuration(value)}` : undefined);

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Usługi i magazyn' }]}
        title="Usługi i cennik"
        description={headerDescription}
        actions={
          canManage ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
              Dodaj usługę
            </Button>
          ) : undefined
        }
      />

      {error && !dialog && !consumptionDialog && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      {loading ? (
        <LoadingState variant="table" rows={5} />
      ) : services.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<ContentCutOutlinedIcon />}
            title="Cennik jest jeszcze pusty"
            description={
              canManage
                ? 'Dodaj pierwszą usługę z ceną i czasem trwania, aby można było umawiać na nią wizyty.'
                : 'Usługi pojawią się tutaj, gdy właściciel salonu doda je do cennika.'
            }
          />
        </SectionCard>
      ) : (
        <Stack gap={2.5}>
          {groups.map(([category, items]) => (
            <SectionCard
              key={category}
              title={category}
              subtitle={countLabel(items.length, 'usługa', 'usługi', 'usług')}
              padding="none"
            >
              <Stack divider={<Box sx={{ borderTop: `1px solid ${tokens.line}` }} />}>
                {items.map(renderServiceRow)}
              </Stack>
            </SectionCard>
          ))}
        </Stack>
      )}

      {/* Dodawanie i edycja uslugi */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edytuj usługę' : 'Dodaj usługę'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <DialogSection first title="Podstawowe informacje">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Nazwa usługi"
                  placeholder="np. Strzyżenie damskie"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={usedCategories}
                  value={form.category}
                  onChange={(_, v) => setForm({ ...form, category: (v as string) || '' })}
                  onInputChange={(_, v) => setForm({ ...form, category: v })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Kategoria"
                      placeholder="np. Fryzjerstwo"
                      helperText={
                        usedCategories.length > 0
                          ? 'Wybierz z użytych wcześniej lub wpisz nową'
                          : 'Wpisz nazwę — kolejne usługi będą ją podpowiadać'
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Opis"
                  sx={{ '& .MuiInputBase-inputMultiline': { p: 0 } }}
                  placeholder="np. Mycie, strzyżenie i modelowanie"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogSection>

          {/* Tryb ceny (uwaga klienta 8) */}
          <DialogSection title="Cena i czas trwania">
            <ToggleButtonGroup
              exclusive
              fullWidth
              aria-label="Sposób ustalania ceny"
              value={pricingMode}
              onChange={(_: unknown, v: PricingMode | null) => v && setPricingMode(v)}
            >
              <ToggleButton value="FIXED">Cena stała</ToggleButton>
              <ToggleButton value="RANGE">Widełki</ToggleButton>
              <ToggleButton value="VARIANTS">Warianty</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" sx={{ color: tokens.muted, mt: 1, mb: 2 }}>
              {PRICING_MODE_HINT[pricingMode]}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={pricingMode === 'RANGE' ? 'Cena od' : pricingMode === 'VARIANTS' ? 'Cena bazowa' : 'Cena'}
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  inputProps={{ min: 0 }}
                  InputProps={unitAdornment('zł')}
                  helperText={pricingMode === 'VARIANTS' ? 'Orientacyjna, zanim zostanie wybrany wariant' : undefined}
                />
              </Grid>
              {pricingMode === 'RANGE' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cena do"
                    type="number"
                    value={form.priceMax}
                    onChange={(e) => setForm({ ...form, priceMax: e.target.value })}
                    inputProps={{ min: 0 }}
                    InputProps={unitAdornment('zł')}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={pricingMode === 'RANGE' ? 'Czas od' : pricingMode === 'VARIANTS' ? 'Czas bazowy' : 'Czas trwania'}
                  type="number"
                  value={form.duration}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  inputProps={{ min: 0 }}
                  InputProps={unitAdornment('min')}
                  helperText={durationHint(form.duration)}
                />
              </Grid>
              {pricingMode === 'RANGE' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Czas do"
                    type="number"
                    value={form.durationMax}
                    onChange={(e) => setForm({ ...form, durationMax: e.target.value })}
                    inputProps={{ min: 0 }}
                    InputProps={unitAdornment('min')}
                    helperText={durationHint(form.durationMax)}
                  />
                </Grid>
              )}
            </Grid>

            {pricingMode === 'VARIANTS' && (
              <Box sx={{ mt: 2.5 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>Warianty</Typography>
                {variants.length === 0 && (
                  <Typography variant="body2" sx={{ color: tokens.muted, mb: 1 }}>
                    Nie ma jeszcze wariantów. Bez nich usługa zapisze się z ceną stałą.
                  </Typography>
                )}
                <Stack gap={1.25}>
                  {variants.map((v, i) => (
                    <Box
                      key={v.id || i}
                      sx={{
                        display: 'grid',
                        gap: 1.5,
                        alignItems: 'end',
                        gridTemplateColumns: {
                          xs: 'minmax(0, 1fr) minmax(0, 1fr) auto',
                          sm: 'minmax(0, 1fr) 128px 118px auto',
                        },
                        p: 1.5,
                        borderRadius: '12px',
                        bgcolor: tokens.surface,
                        border: `1px solid ${tokens.line}`,
                      }}
                    >
                      <TextField
                        fullWidth
                        label="Nazwa wariantu"
                        placeholder="np. Włosy długie"
                        value={v.name}
                        onChange={(e) => updateVariant(i, { name: e.target.value })}
                        sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                      />
                      <TextField
                        fullWidth
                        label="Cena"
                        type="number"
                        value={v.price}
                        onChange={(e) => updateVariant(i, { price: e.target.value })}
                        inputProps={{ min: 0 }}
                        InputProps={unitAdornment('zł')}
                      />
                      <TextField
                        fullWidth
                        label="Czas"
                        type="number"
                        value={v.duration}
                        onChange={(e) => updateVariant(i, { duration: e.target.value })}
                        inputProps={{ min: 0 }}
                        InputProps={unitAdornment('min')}
                      />
                      <Tooltip title="Usuń wariant">
                        <IconButton
                          aria-label="Usuń wariant"
                          onClick={() => setVariants((prev) => prev.filter((_, j) => j !== i))}
                          sx={{ mb: 0.25 }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Stack>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setVariants((prev) => [...prev, { name: '', price: '', duration: '' }])}
                  sx={{ mt: 1 }}
                >
                  Dodaj wariant
                </Button>
              </Box>
            )}
          </DialogSection>

          <DialogSection
            title="Zużycie produktów"
            description="Zaznacz produkty zużywane przy tej usłudze. Bez podanej ilości przyjmiemy 1 jednostkę. Zużycie na klienta lub na ml ustawisz w oknie „Zużycie produktów” na liście usług."
          >
            {dialogProductsLoading ? (
              <Stack gap={1}>
                {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={48} />)}
              </Stack>
            ) : dialogProducts.length === 0 ? (
              <Box sx={{ border: `1px dashed ${tokens.lineStrong}`, borderRadius: '12px', px: 2, py: 1.75 }}>
                <Typography variant="body2" sx={{ color: tokens.muted }}>
                  W salonie nie ma jeszcze produktów — dodasz je w sekcji Usługi i magazyn → Produkty.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ border: `1px solid ${tokens.line}`, borderRadius: '12px', overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) 170px' },
                    gap: 1.5,
                    px: 1.75,
                    py: 1,
                    bgcolor: tokens.surface,
                    borderBottom: `1px solid ${tokens.lineStrong}`,
                  }}
                >
                  <Typography sx={headCellSx}>
                    Produkt{dialogSelectedCount ? ` · zaznaczone: ${dialogSelectedCount}` : ''}
                  </Typography>
                  <Typography sx={{ ...headCellSx, display: { xs: 'none', sm: 'block' } }}>Ilość na usługę</Typography>
                </Box>
                {dialogProducts.map((product) => {
                  const enabled = Boolean(dialogConsumptionForm[product.id]?.enabled);
                  return (
                    <Box
                      key={product.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', sm: 'auto minmax(0, 1fr) 170px' },
                        columnGap: 0.75,
                        rowGap: 0.5,
                        alignItems: 'center',
                        pl: 0.75,
                        pr: 1.75,
                        py: 0.75,
                        '&:not(:last-of-type)': { borderBottom: `1px solid ${tokens.line}` },
                      }}
                    >
                      <Checkbox
                        checked={enabled}
                        onChange={(e) => updateDialogConsumption(product.id, { enabled: e.target.checked })}
                        inputProps={{ 'aria-label': `Użyj produktu ${product.name}` }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{product.name}</Typography>
                        <Typography variant="caption" component="div">
                          {product.sku ? `SKU ${product.sku}` : 'Bez SKU'} · {product.unit}
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        disabled={!enabled}
                        value={dialogConsumptionForm[product.id]?.quantity ?? ''}
                        onChange={(e) => updateDialogConsumption(product.id, { enabled: true, quantity: e.target.value })}
                        inputProps={{ min: 0, step: 0.001, 'aria-label': `Ilość na usługę: ${product.name}` }}
                        InputProps={unitAdornment(product.unit)}
                        placeholder="domyślnie 1"
                        sx={{ gridColumn: { xs: '2 / -1', sm: 'auto' }, mb: { xs: 0.75, sm: 0 } }}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          </DialogSection>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDialog(false)}>Anuluj</Button>
          <Button
            variant="contained"
            onClick={onSaveService}
            disabled={saving || dialogProductsLoading}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {editId ? 'Zapisz zmiany' : 'Dodaj usługę'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Zuzycie produktow dla uslugi */}
      <Dialog open={consumptionDialog} onClose={closeConsumptions} maxWidth="md" fullWidth>
        <DialogTitle>
          Zużycie produktów
          {consumptionService && (
            <Box component="span" sx={{ display: 'block', mt: 0.25, fontSize: '0.9375rem', fontWeight: 500, color: tokens.muted }}>
              {consumptionService.name}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {consumptionLoading ? (
            <Stack gap={1.5} sx={{ py: 1 }}>
              {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={104} />)}
            </Stack>
          ) : (
            <Stack gap={2}>
              <Alert severity="info">
                Dla każdego produktu podaj zużycie bazowe albo zużycie na klienta lub na ml. Bez zużycia bazowego
                system wyliczy je sam: (na klienta lub na ml) × liczba klientów (domyślnie 1). Puste wiersze nie
                zostaną zapisane.
              </Alert>

              {hasInvalidConsumptionRows && (
                <Alert severity="warning">
                  Część wierszy ma niepełne dane. Aby zapisać produkt, podaj źródło zużycia (bazowe, na klienta
                  albo na ml) oraz poprawną liczbę klientów.
                </Alert>
              )}

              {consumptionProducts.length === 0 ? (
                <EmptyState
                  compact
                  icon={<Inventory2OutlinedIcon />}
                  title="Brak produktów w salonie"
                  description="Dodaj produkty w sekcji Usługi i magazyn → Produkty, a potem przypisz je do usługi."
                />
              ) : (
                <Box sx={{ border: `1px solid ${tokens.line}`, borderRadius: '12px' }}>
                  {consumptionProducts.map((product) => {
                    const rowValue = consumptionForm[product.id];
                    const computation = computeConsumption(rowValue);
                    const showRowError =
                      computation.hasAnyInput &&
                      (!computation.hasDriver ||
                        !computation.hasValidClientsCount ||
                        !computation.calculatedQuantity);
                    const driverError = showRowError && !computation.hasDriver;
                    const rowState: { label: string; tone: PillTone } = showRowError
                      ? { label: 'Uzupełnij dane', tone: 'danger' }
                      : computation.calculatedQuantity
                        ? { label: `Wyliczona ilość: ${formatQuantity(computation.calculatedQuantity)} ${product.unit}`, tone: 'plum' }
                        : { label: 'Nieprzypisany', tone: 'neutral' };
                    const meta = [product.category, product.sku ? `SKU ${product.sku}` : null, `jednostka: ${product.unit}`]
                      .filter(Boolean)
                      .join(' · ');

                    return (
                      <Box
                        key={product.id}
                        sx={{
                          px: { xs: 1.5, sm: 2 },
                          py: 1.75,
                          '&:not(:last-of-type)': { borderBottom: `1px solid ${tokens.line}` },
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          justifyContent="space-between"
                          gap={1}
                          sx={{ mb: 1.5 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem' }}>{product.name}</Typography>
                            <Typography variant="caption" component="div">{meta}</Typography>
                          </Box>
                          <StatusPill label={rowState.label} tone={rowState.tone} size="small" />
                        </Stack>

                        <Box
                          sx={{
                            display: 'grid',
                            gap: 1.5,
                            alignItems: 'start',
                            gridTemplateColumns: {
                              xs: 'repeat(2, minmax(0, 1fr))',
                              md: 'repeat(4, minmax(0, 1fr)) minmax(0, 1.4fr)',
                            },
                          }}
                        >
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            label="Zużycie bazowe"
                            value={rowValue?.quantity ?? ''}
                            onChange={(e) => updateConsumption(product.id, { quantity: e.target.value })}
                            error={driverError}
                            inputProps={{ min: 0, step: 0.001 }}
                            InputProps={unitAdornment(product.unit)}
                            placeholder="np. 30"
                          />
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            label="Na klienta"
                            value={rowValue?.perClient ?? ''}
                            onChange={(e) => updateConsumption(product.id, { perClient: e.target.value })}
                            error={driverError}
                            inputProps={{ min: 0, step: 0.001 }}
                            InputProps={unitAdornment(product.unit)}
                            placeholder="—"
                          />
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            label="Na ml"
                            value={rowValue?.perMl ?? ''}
                            onChange={(e) => updateConsumption(product.id, { perMl: e.target.value })}
                            error={driverError}
                            inputProps={{ min: 0, step: 0.001 }}
                            placeholder="—"
                          />
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            label="Liczba klientów"
                            value={rowValue?.clientsCount ?? ''}
                            onChange={(e) => updateConsumption(product.id, { clientsCount: e.target.value })}
                            error={showRowError && (rowValue?.clientsCount ?? '').trim().length > 0 && !computation.hasValidClientsCount}
                            inputProps={{ min: 0, step: 1 }}
                            placeholder="domyślnie 1"
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="Uwagi"
                            value={rowValue?.note ?? ''}
                            onChange={(e) => updateConsumption(product.id, { note: e.target.value })}
                            inputProps={{ maxLength: 200 }}
                            placeholder="np. włosy długie"
                            sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}
                          />
                        </Box>

                        {showRowError && (
                          <Typography variant="caption" component="div" sx={{ mt: 1, color: 'error.main', fontWeight: 600 }}>
                            {driverError
                              ? 'Podaj zużycie bazowe, na klienta albo na ml.'
                              : 'Niepoprawna konfiguracja: ustaw źródło zużycia i poprawną liczbę klientów.'}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeConsumptions}>Anuluj</Button>
          <Button
            variant="contained"
            onClick={saveConsumptions}
            disabled={consumptionSaving || consumptionLoading}
            startIcon={consumptionSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {consumptionSaving ? 'Zapisywanie…' : 'Zapisz zużycie'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
